import React from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDAlert,
  FDPrimaryActionButton, FDStack,
} from '@facet-wealth/design';
import { DialogActions, DialogContent, DialogTitle } from 'Views/Page/Content/Modal/Components';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import fr from 'facet-rest';
import { LottieRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/LottieRel';
import { startRegistration } from '@simplewebauthn/browser';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useAuthenticationFactorListContext } from 'Organisms/AuthenticationFactorList/AuthenticationFactorListContext';
import { dispatchAuthenticationFactorsRefresh } from 'Hooks/useRefreshEvent';
import {
  dataAttrs,
} from 'Helpers/Testing';
import {
  useIdentityChallengeContext,
} from 'Context/IdentityChallengeContext';
import sentry
  from 'Integrations/Sentry';


const Layout = ({ onSuccess }) => {
  const {
    resource, consumeAttr,
  } = useMetaLayoutContext();
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();
  const { refreshListCollection } = useAuthenticationFactorListContext();
  const { handleInterceptResponse } = useIdentityChallengeContext();

  const handleRegisterPasskey = async (rel) => {
    try {
      // Begin registration: get the challenge from the server
      const [registration] = await rel.link('related').get();
      const challenge = await registration.submit();

      await handleInterceptResponse(challenge, async ([originalRequestResponse]) => {
        // Tell the browser to prompt the user to create a key pair
        const credential = await startRegistration({ optionsJSON: originalRequestResponse.attr('options').publicKey });

        // Send the public key to the server.
        // This uses fetch because the webauthn library requires a full HTTP request to be passed in, so they
        // dictate everything about it.  Our normal FR stuff won't be recognized by the webauthn lib.
        const res = await fetch(originalRequestResponse.relationship('publicKey').link('related').attr('url'), {
          credentials: 'include',
          mode: 'cors',
          method: 'POST',
          body: JSON.stringify(credential),
        });

        if (!res.ok) {
          throw new Error('failed to register public key with server');
        }

        displaySuccess('Passkey registered');

        // Submit the wizard step
        onSuccess();

        // Refresh the auth factors list to show the new passkey
        refreshListCollection();
        dispatchAuthenticationFactorsRefresh();
      });
    } catch (err) {
      sentry.createSentryErrorLog(err);
      displayErrorMessage(err, 'Failed to register passkey');
    }
  };

  return (
    <>
      <DialogTitle value={consumeAttr('title')} />
      <DialogContent>
        <FDStack direction={'column'} alignItems={'center'} spacing={2}>
          {resource.withRelationship(
            fr.relationships.lottie.name,
            (rel) => (<LottieRel relationship={rel} width={'100%'} />),
            () => null,
          )}
          <MetaControlField attr={'body'} />
          <FDAlert status={'info'} title={consumeAttr('alert')} />
        </FDStack>
      </DialogContent>
      <DialogActions>
        {resource.withRelationship(fr.relationships.beginRegistration.name, (rel) => (
          <FDPrimaryActionButton
            {...dataAttrs('register-passkey-btn')}
            onClick={() => handleRegisterPasskey(rel)}
            label={rel.meta('prompt')}
            icon={rel.meta('icon')}
            iconPosition={'start'}
          />), () => null)}
      </DialogActions>
    </>
  );
};

Layout.defaultProps = {
};

Layout.propTypes = {
};

export default Layout;
